import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import NewsCard from "@/components/base/NewsCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import { newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

const PAGE_STEP = 5;

export default function NewsPage() {
  useSeo({
    title: "影视资讯 - 最新行业动态与剧集新闻 - 赏金女王",
    description:
      "赏金女王影视资讯频道，提供最新影视行业动态、剧集新闻、票房数据与海外发行资讯，帮助影视爱好者及时了解行业变化。",
    keywords: "影视资讯,影视新闻,行业动态,剧集新闻,赏金女王",
  });

  const [keyword, setKeyword] = useState("");
  const [visible, setVisible] = useState(PAGE_STEP);

  const sorted = useMemo(() => [...newsList].sort((a, b) => b.date.localeCompare(a.date)), []);

  const filtered = useMemo(() => {
    const k = keyword.trim();
    if (!k) return sorted;
    return sorted.filter(
      (item) => item.title.includes(k) || item.summary.includes(k) || item.category.includes(k)
    );
  }, [keyword, sorted]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <div className="mb-6">
        <Breadcrumb items={[{ name: "首页", path: "/" }, { name: "影视资讯" }]} />
      </div>

      <header className="mb-6 md:mb-8">
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">影视资讯</h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          汇集最新影视行业动态、剧集新闻与市场观察，帮助影视爱好者第一时间了解行业变化与作品动向。
        </p>
      </header>

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setVisible(PAGE_STEP);
          }}
          className="relative w-full sm:max-w-sm"
        >
          <i
            className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"
            aria-hidden="true"
          ></i>
          <input
            type="text"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setVisible(PAGE_STEP);
            }}
            placeholder="搜索资讯标题关键词"
            aria-label="搜索资讯标题关键词"
            className="w-full pl-9 pr-3 py-2.5 text-sm rounded-full bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </form>
        <span className="text-sm text-foreground-500">
          共 <strong className="text-accent-600">{filtered.length}</strong> 条资讯
        </span>
      </div>

      {shown.length > 0 ? (
        <>
          <div className="space-y-3 md:space-y-4">
            {shown.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
          {hasMore && (
            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_STEP)}
                className="px-6 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-700 hover:bg-background-100 hover:border-accent-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                加载更多资讯
                <i className="ri-arrow-down-s-line ml-1" aria-hidden="true"></i>
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <span className="w-14 h-14 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-2xl mb-4">
            <i className="ri-search-eye-line" aria-hidden="true"></i>
          </span>
          <p className="text-foreground-700 font-medium">没有找到相关的影视资讯</p>
          <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或浏览全部资讯</p>
          <button
            type="button"
            onClick={() => {
              setKeyword("");
              setVisible(PAGE_STEP);
            }}
            className="mt-6 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            查看全部资讯
          </button>
        </div>
      )}
    </div>
  );
}